import { Sale } from "@towersystems/roam-common/lib/generated-types";
import { Colors, Spacings, View } from "react-native-ui-lib";
import { Text, TextButton, Tooltip } from "@towersystems/roam-common-native";
import { FlatListSearchable } from "@towersystems/roam-common-native";
import { useSearchQueryProvider } from "../../../../utilities/search";
import { SaleResourceListItem } from "../SaleResourceListItem";
import { DEFAULT_PAGE_WIDTH } from "@towersystems/roam-common-native";
import React from "react";
import {
  getDateRangeForGroup,
  groupItemsByRecentDays,
  GroupLabel,
} from "../../../../utilities/group-obj-by-date";
import { formatDate, HTML5_FMT } from "../../../../utilities/date/format-date";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

type GroupedItem = {
  label: string;
  data: Sale[];
};

export interface SalesResourceListProps {
  loading?: boolean;
  limit?: number;
  items: Sale[];
  onEndReached?: () => void;
  hideSearch?: boolean;
}

export const SalesResourceList = ({
  items,
  limit = 20,
  loading,
  onEndReached,
  hideSearch = false,
}: SalesResourceListProps) => {
  const { query, setQuery } = useSearchQueryProvider();
  const navigation = useNavigation<any>();
  const { strategyEnabled } = useLlmQuickWinsProvider();

  const groupedData: GroupedItem[] = React.useMemo(() => {
    const flatGroups = groupItemsByRecentDays(items);
    const groups: { [label: string]: Sale[] } = {};
    let currentLabel = "";
    flatGroups.forEach((item) => {
      if (item.type === "header") {
        currentLabel = item.label;
        if (!groups[currentLabel]) groups[currentLabel] = [];
      } else {
        groups[currentLabel].push(item);
      }
    });
    return Object.entries(groups).map(([label, data]) => ({ label, data }));
  }, [items]);

  const renderGroupCard = ({ item }: { item: GroupedItem }) => {
    let header = item.label;

    const { start, end, endInclusive } = getDateRangeForGroup(
      item.label as GroupLabel,
    );

    if (item.label === "Today") {
      header = `Today, ${formatDate(
        new Date().toISOString(),
        HTML5_FMT.MONTH_DAY,
      )}`;
    }

    return (
      <View>
        <View style={{ marginBottom: 28, marginTop: 16 }}>
          {start && strategyEnabled("sales_period_summary_quick_win") ? (
            <Tooltip text="Summarize with AI">
              <View>
                <TextButton
                  icon={<Ionicons name="sparkles" size={12} />}
                  textStyle={{
                    fontSize: 16,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    color: Colors.text,
                    opacity: 0.6,
                  }}
                  onPress={() =>
                    navigation.navigate("AiInsightsRootModalScreen", {
                      screen: "AiInsightsScreen",
                      startDate: start,
                      endDate: endInclusive,
                    })
                  }
                >
                  {header}
                </TextButton>
              </View>
            </Tooltip>
          ) : (
            <Text
              style={{
                fontSize: 16,
                fontWeight: "bold",
                textTransform: "uppercase",

                color: Colors.text,
                opacity: 0.6,
              }}
            >
              {header}
            </Text>
          )}
        </View>

        <View>
          {item.data.map((sale, idx) => {
            const isLastItem = idx === item.data.length - 1;

            return (
              <View
                key={idx}
                style={{
                  marginBottom: 16,
                  borderBottomWidth: isLastItem ? 0 : 1,
                  paddingBottom: 16,
                  borderBottomColor: Colors.borderColor,
                }}
              >
                <SaleResourceListItem sale={sale} />
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  return (
    <FlatListSearchable
      placeholder="Search..."
      style={{
        marginBottom: Spacings.s5,
      }}
      contentContainerStyle={{
        maxWidth: DEFAULT_PAGE_WIDTH,
        width: "100%",
        alignSelf: "center",
        paddingHorizontal: Spacings.s5,
        paddingTop: Spacings.s5,
      }}
      searchBarContainerStyle={{ marginBottom: Spacings.s4 }}
      onChange={(query) => setQuery(query)}
      value={query}
      renderItem={renderGroupCard}
      ItemSeparatorComponent={() => <View />}
      refreshing={loading}
      data={groupedData as any}
      scrollEnabled={true}
      nestedScrollEnabled
      renderSearchBarHeader={hideSearch ? () => <View /> : undefined}
      ListEmptyComponent={<Text>No sales to show</Text>}
      onEndReachedThreshold={0.3}
      onEndReached={() => {
        items.length >= 1 && onEndReached && onEndReached();
      }}
    ></FlatListSearchable>
  );
};
